/* eslint-disable max-lines */
import { action } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';

import { AnalyticsStore } from 'bernie-core';
import { ClickTracker } from 'bernie-analytics';
import { Context } from 'bernie-context';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';

import { CurrentHotelStore } from 'src/common/stores/current-hotel-store';
import { MultiItemStore } from 'src/common/stores/multi-item-store';

import { Level, liveAnnounce } from 'uitk-react-utils';
import { ToolbarType, UitkToolbar } from 'uitk-react-toolbar';
import { UitkButtonSize, UitkFloatingFullWidthButton, UitkTertiaryButton } from 'uitk-react-button';
import { UitkDialogAnimationType, UitkFullscreenDialog } from 'uitk-react-dialog';
import { UitkIcon } from 'uitk-react-icons';
import { UitkLink } from 'uitk-react-link';
import { UitkRadio, UitkRadioGroup } from 'uitk-react-switch';
import { UitkScrim, UitkScrimType } from 'uitk-react-scrim';

import { MultiItemRoom } from 'components/common/room-picker/internals/multi-item-room';
import { Room } from './internals/room';
import { RoomPickerModel } from 'components/common/room-picker/room-picker-model';

const SubmitButtonTracked = ClickTracker(UitkFloatingFullWidthButton);
const TertiaryButtonTracked = ClickTracker(UitkTertiaryButton);

enum InfantSeating {
  IN_SEAT = 'seat',
  ON_LAP = 'lap',
}

export interface RoomPickerProps extends LocalizedComponentProps {
  adultsString: string;
  analytics: AnalyticsStore;
  buttonText: string;
  childrenString: string;
  onDismiss?: () => void;
  onSave?: (adults: string, children: string, value: string, infantsInSeats?: string) => void;
  currentHotel?: CurrentHotelStore;
  context?: Context;
  isPackageShoppingPath?: boolean;
  infantSeating?: InfantSeating;
  multiItem?: MultiItemStore;
  infantsInSeats?: string;
}

export interface RoomPickerState {
  dialogShow: boolean;
  selection: RoomPickerModel;
  justAddedRoom: boolean;
  justRemovedRoom: boolean;
  infantsInSeats?: string;
}

@withLocalization
@inject('currentHotel', 'context', 'multiItem')
@observer
export class RoomPicker extends React.Component<RoomPickerProps, RoomPickerState> {
  private focusOnOpen: React.RefObject<HTMLButtonElement> = React.createRef();
  private lastRoomHeader: React.RefObject<HTMLHeadingElement> = React.createRef();

  constructor(props) {
    super(props);

    this.state = {
      dialogShow: false,
      selection: new RoomPickerModel(props, props.l10n, props.isPackageShoppingPath),
      justAddedRoom: false,
      justRemovedRoom: false,
    };

    this.onAddRoom = this.onAddRoom.bind(this);
    this.onChildAgeUpdated = this.onChildAgeUpdated.bind(this);
    this.onRemoveRoom = this.onRemoveRoom.bind(this);
    this.onRoomAdultsChanged = this.onRoomAdultsChanged.bind(this);
    this.onRoomChildrenChanged = this.onRoomChildrenChanged.bind(this);
    this.onRoomInfantsChanged = this.onRoomInfantsChanged.bind(this);
    this.onInfantAgeUpdated = this.onInfantAgeUpdated.bind(this);
    this.onDismiss = this.onDismiss.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.resetLiveAnnounce = this.resetLiveAnnounce.bind(this);
  }

  public static defaultRoomProps = {
    adultsString: '2',
    childrenString: '',
  };

  public componentWillMount() {
    this.updateStateWithProps(this.props);
  }

  public componentWillReceiveProps(nextProps) {
    this.updateStateWithProps(nextProps);
  }

  public componentDidMount() {
    this.setState({ dialogShow: true });
  }

  public componentDidUpdate() {
    const { l10n } = this.props;
    const { selection } = this.state;
    const { roomCount } = selection;

    if (this.state.justAddedRoom) {
      liveAnnounce(l10n.formatText('hotels.roomPicker.roomAdded.liveAnnounce', roomCount));
      this.setFocusOnLastRoomHeader();
      this.setState({ justAddedRoom: false });
    }

    if (this.state.justRemovedRoom) {
      liveAnnounce(l10n.formatText('hotels.roomPicker.roomRemoved.liveAnnounce', roomCount));
      this.setFocusOnLastRoomHeader();
      this.setState({ justRemovedRoom: false });
    }
  }

  public setFocusOnLastRoomHeader() {
    if (this.lastRoomHeader.current && typeof this.lastRoomHeader.current.focus === 'function') {
      this.lastRoomHeader.current.focus();
    }
  }

  private updateStateWithProps(props: RoomPickerProps) {
    const { adults, children } = this.state.selection.initialProps;

    if (adults !== props.adultsString || children !== props.childrenString) {
      this.state.selection.setupRooms(props.adultsString, props.childrenString);
    }
  }

  public render() {
    const { l10n } = this.props;
    const { dialogShow, selection } = this.state;
    const { disableAddRoom, displaySummary, rooms, enableInfantSeatSelection } = selection;

    const toolBarAttributes = {
      iconLabel: l10n.formatText('hotels.roomPicker.cancelButton.a11yLabel'),
      header: this.props.isPackageShoppingPath
        ? l10n.formatText('hotels.travelers.multiitem.dialog.label')
        : l10n.formatText('hotels.roomPicker.title'),
      type: ToolbarType.CLOSE,
    };

    const infantSeatChanged = event => this.onInfantSeatingChanged(event);

    const toolbarAnalytics = this.props.analytics.setupTracker(':ChangeRoomGuest', 'Close Room Picker');

    const submitActionText: string = this.props.buttonText ? this.props.buttonText : l10n.formatText('hotels.roomPicker.doneButton.label');

    const submitButtonText: JSX.Element = (
      <>
        {submitActionText}
        <span className="is-visually-hidden">{l10n.formatText('hotels.roomPicker.doneButton.a11yLabel')}</span>
      </>
    );

    const pickerMainClasses: string[] = [
      'uitk-cell',
      'all-x-padding-four',
      'all-t-padding-half',
      'all-b-padding-twelve',
      'uitk-scroll-vertical',
    ];

    const showInfantSeatingOptions =
      this.props.isPackageShoppingPath && this.props.multiItem.isFlightPackage() && enableInfantSeatSelection;

    return (
      <UitkFullscreenDialog
        animation={UitkDialogAnimationType.SLIDEUP}
        className="uitk-grid uitk-grid-cell all-cell-1-1"
        dialogShow={dialogShow}
      >
        <div className="uitk-cell">
          <UitkToolbar {...toolBarAttributes} action={this.onDismiss} analytics={toolbarAnalytics} buttonRef={this.focusOnOpen} />
        </div>
        <section className={pickerMainClasses.join(' ')}>
          {rooms.map((room, roomIndex) => this.getRoomComponent(roomIndex, room, selection))}
          <div className="uitk-type-right all-t-padding-four all-b-padding-twelve">
            <TertiaryButtonTracked
              data-stid="add-room"
              linkName="Add room"
              onClick={this.onAddRoom}
              disabled={disableAddRoom}
              rfrr=":AddRoom"
            >
              {l10n.formatText('hotels.roomPicker.addRoomButton.label')}
            </TertiaryButtonTracked>
          </div>
          {showInfantSeatingOptions && (
            <div className="airlineChildSeating uitk-cell all-cell-1-1 all-y-gutter-one">
              <fieldset>
                <legend className="uitk-type-300 uitk-type-bold">
                  {l10n.formatText('hotels.roomPicker.multiitem.infantSeating.label')}
                </legend>
                <UitkRadioGroup
                  group="ChildSeating"
                  onChange={infantSeatChanged}
                  selected={this.props.infantsInSeats === '1' ? InfantSeating.IN_SEAT : InfantSeating.ON_LAP}
                >
                  <UitkRadio id="ChildOnLap" labelClassName="all-r-padding-six" value={'lap'}>
                    {l10n.formatText('hotels.roomPicker.multiitem.infantSeating.inlap.label')}
                  </UitkRadio>
                  <UitkRadio id="ChildInSeat" labelClassName="all-r-padding-six" value={'seat'}>
                    {l10n.formatText('hotels.roomPicker.multiitem.infantSeating.inseat.label')}
                  </UitkRadio>
                </UitkRadioGroup>
              </fieldset>
            </div>
          )}
          {showInfantSeatingOptions && (
            <div className="uitk-cell all-cell-1-1">
              <UitkLink size="small" className="airlineAgeRules">
                <a href="/p/info-main/h_fw_default?rhtx=HTX_FLTSRCH_RT#travs" id="airline-age-rules" target="_blank" rel="noopener">
                  {l10n.formatText('hotels.roomPicker.multiitem.airlineRules.text')}
                  <UitkIcon id="airline-age-rules-opens-in-new" name="open_in_new" title="Opens in a new window" />
                </a>
              </UitkLink>
            </div>
          )}
          <div className="all-t-padding-two all-b-padding-six" />
        </section>
        <UitkScrim type={UitkScrimType.BUTTON}>
          <SubmitButtonTracked
            isFullWidth={true}
            size={UitkButtonSize.LARGE}
            data-stid="apply-room-picker"
            linkName="Apply Room Picker"
            onClick={this.onSubmit}
            rfrr=":ChangeRoomGuestDone"
            subText={displaySummary}
          >
            {submitButtonText}
          </SubmitButtonTracked>
        </UitkScrim>
      </UitkFullscreenDialog>
    );
  }

  @action
  private onRoomAdultsChanged(room, e): void {
    const num: number = parseInt(e.target.value, 10);

    if (!isNaN(num)) {
      this.state.selection.changeAdultsForRoom(room, num);
    }
  }

  @action
  private onRoomChildrenChanged(room, e): void {
    const num: number = parseInt(e.target.value, 10);
    if (!isNaN(num)) {
      this.state.selection.changeChildrenForRoom(room, num);
    }
  }

  @action
  private onRoomInfantsChanged(room, e): void {
    const num: number = parseInt(e.target.value, 10);
    if (!isNaN(num)) {
      this.state.selection.changeChildrenForRoom(room, num, true);
    }
  }

  @action
  private onAddRoom(): void {
    this.state.selection.addRoom();
    this.setState({ justAddedRoom: true });
  }

  @action
  private onRemoveRoom(room): void {
    this.state.selection.removeRoom(room);
    this.setState({ justRemovedRoom: true });
  }

  @action
  private onChildAgeUpdated(roomIndex: number, childIndex: number, target) {
    const { analytics, l10n } = this.props;
    const age: number = target.value;

    this.state.selection.changeChildAge(roomIndex, childIndex, age);
    target.focus();
    liveAnnounce(l10n.formatText('hotels.roomPicker.childUpdated.liveAnnounce', childIndex, age), Level.POLITE);

    if (analytics && typeof analytics.trackEvent === 'function') {
      const roomNum: number = roomIndex + 1;
      const childNum: number = childIndex + 1;
      analytics.trackEvent(`:ROOM.${roomNum}.Child.${childNum}.AGE.${age}`, 'Child age update', null);
    }
  }

  @action
  private onInfantAgeUpdated(roomIndex: number, childIndex: number, target) {
    const { analytics, l10n } = this.props;
    const age: number = target.value;

    this.state.selection.changeChildAge(roomIndex, childIndex, age, true);
    target.focus();
    liveAnnounce(l10n.formatText('hotels.roomPicker.childUpdated.liveAnnounce', childIndex, age), Level.POLITE);

    if (analytics && typeof analytics.trackEvent === 'function') {
      const roomNum: number = roomIndex + 1;
      const childNum: number = childIndex + 1;
      analytics.trackEvent(`:ROOM.${roomNum}.Infant.${childNum}.AGE.${age}`, 'Infant age update', null);
    }
  }

  @action
  private onInfantSeatingChanged(event) {
    const { analytics } = this.props;
    const seatingChoice = event.target.value;

    this.setState({ infantsInSeats: seatingChoice === InfantSeating.ON_LAP ? '0' : '1' });

    if (analytics && typeof analytics.trackEvent === 'function') {
      analytics.trackEvent(`:Infant.Seating.${seatingChoice}`, 'Infant seating update', null);
    }
  }

  private resetLiveAnnounce(): void {
    liveAnnounce('', Level.ASSERTIVE);
  }

  private onDismiss(): void {
    const { onDismiss } = this.props;

    this.state.selection.reset();
    this.resetLiveAnnounce();

    if (onDismiss) {
      onDismiss();
    }
  }

  private onSubmit(): void {
    const { onSave } = this.props;

    if (onSave) {
      const { finalProps, displaySummary } = this.state.selection;

      onSave(finalProps.adults, finalProps.children, displaySummary, this.state.infantsInSeats);
    }

    this.resetLiveAnnounce();
  }

  private getRoomComponent(roomIndex, room, selection) {
    if (this.props.isPackageShoppingPath) {
      return (
        <MultiItemRoom
          analytics={this.props.analytics}
          key={roomIndex}
          l10n={this.props.l10n}
          headerRef={this.lastRoomHeader}
          onChildAgeUpdated={this.onChildAgeUpdated}
          onRoomAdultsChanged={this.onRoomAdultsChanged}
          onRoomChildrenChanged={this.onRoomChildrenChanged}
          onRoomInfantsChanged={this.onRoomInfantsChanged}
          onInfantAgeUpdated={this.onInfantAgeUpdated}
          onRemoveRoom={this.onRemoveRoom}
          room={room}
          roomIndex={roomIndex}
          selection={selection}
        />
      );
    }

    return (
      <Room
        analytics={this.props.analytics}
        key={roomIndex}
        l10n={this.props.l10n}
        headerRef={this.lastRoomHeader}
        onChildAgeUpdated={this.onChildAgeUpdated}
        onRoomAdultsChanged={this.onRoomAdultsChanged}
        onRoomChildrenChanged={this.onRoomChildrenChanged}
        onRemoveRoom={this.onRemoveRoom}
        room={room}
        roomIndex={roomIndex}
        selection={selection}
      />
    );
  }
}
